import { Inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { GatewayBase } from "../bases";
import { ConfigService } from "./config.service";
import { ApiType, AppUser, AuthorizationPermission } from "../types";
import { filter, tap } from "rxjs/operators";
import { AppPermisions } from "../app-permissions";
import { LG_USER_INFO } from "@logex/framework/lg-application";

@Injectable({ providedIn: "root" })
export class PermissionService extends GatewayBase {
    public _permissions: string[] = null;
    public _permissionsSubject = new BehaviorSubject<string[]>(null);
    public _permissions$ = this._permissionsSubject.pipe(filter(val => val !== null));

    constructor(
        private _httpClient: HttpClient,
        private _configService: ConfigService,
        @Inject(LG_USER_INFO) private _userInfo: AppUser
    ) {
        super();
    }

    getPermissions(): Observable<AuthorizationPermission[]> {
        return this._httpClient
            .post<AuthorizationPermission[]>(
                `/user/permissions/search`,
                { applicationInstances: [this._configService.configuration.instance] },
                { headers: { type: ApiType.authorization } }
            )
            .pipe(
                tap((permissions: AuthorizationPermission[]) => this._setPermissions(permissions))
            );
    }

    private _setPermissions(permissionResponse: AuthorizationPermission[]): void {
        const appPermissions = permissionResponse.find(
            permission =>
                permission.applicationInstance === this._configService.configuration.instance
        );

        this._userInfo.roles = {};

        if (appPermissions) {
            this._permissions = appPermissions.permissions;
            this._permissions.forEach(x => {
                this._userInfo.roles[x] = true;
            });
            this._permissionsSubject.next(appPermissions.permissions);
        } else {
            this._permissions = [];
            this._permissionsSubject.next([]);
        }
    }

    public hasPermission(expectedPermissions: string[]): boolean {
        if (expectedPermissions == null) return true;

        for (const permission of expectedPermissions) {
            if (this._permissions.indexOf(permission) !== -1) return true;
        }

        return false;
    }

    public get canEditOrganization(): boolean {
        return this.hasPermission([AppPermisions.editOrganisations]);
    }

    public get isAdmin(): boolean {
        return this.hasPermission([AppPermisions.admin]);
    }
}
